import { canonicalDatasetToProcessAdvisorRows } from '@process-advisor/canonical/eventLogBridge';

// Event log transformation logic extracted from processMiner.js
//
// This module is intentionally stateless: it takes inputs and helper functions
// and returns the transformed arrays.

/**
 * Transform an imported event log (array of raw events) into the derived arrays
 * used by the process-miner visualizations.
 *
 * @param {object} args
 * @param {Array} args.importEventLogArray
 * @param {Array} args.taskAttributes
 * @param {Array} args.eventAttributes
 * @param {Array} args.metricArray
 * @param {string} args.planTier
 * @param {string|null} args.sample
 * @param {boolean} args.calendarExists
 * @param {Function} args.calculateDuration
 * @param {Array} args.issueComments
 * @param {Array} args.issueWorklogs
 * @param {Function} args.importArrayActivityID
 * @param {Function} args.importArrayStepUpdate
 * @param {Function} args.arrayVariantID
 *
 * @returns {Promise<object>} transformed arrays
 */
export async function processEventLogTransform(args) {
  const {
    importEventLogArray,
    canonicalDataset,
    taskAttributes,
    eventAttributes,
    metricArray,
    planTier,
    sample,
    calendarExists,
    calculateDuration,
    issueComments,
    issueWorklogs,
    importArrayActivityID,
    importArrayStepUpdate,
    arrayVariantID,
  } = args;

  // Canonical is the preferred Sprint 1 input. Convert at the last possible
  // boundary while the existing miner still expects its legacy row contract.
  // Callers that have not migrated yet can continue supplying importEventLogArray.
  const sourceRows = canonicalDataset
    ? canonicalDatasetToProcessAdvisorRows(canonicalDataset)
    : (importEventLogArray || []);

  // Copy and sort so the caller can keep the original order if needed.
  const cleaned = sourceRows.filter((e) => e && e.task && e.timestamp);
  const sorted = [...cleaned].sort(
    (a, b) => String(a?.task || '').localeCompare(String(b?.task || '')) || String(a?.timestamp || '').localeCompare(String(b?.timestamp || '')),
  );

  let taskArray = [];
  let eventLogArray = [];
  let activityArray = [];
  let activityIndexArray = [];
  let stepArray = [];
  let stepArrayIndex = [];
  let variantArray = [];
  let variantArrayIndex = [];
  let taskEventsArray = [];

  const startLabel = 'Start';
  const endLabel = 'End';
  const activity_separator = ' -> ';

  let obj = importArrayActivityID(activityArray, activityIndexArray, '', startLabel, true, false);
  activityArray = obj[0];
  const activityIDStart = obj[1];
  activityIndexArray = obj[2];

  obj = importArrayActivityID(activityArray, activityIndexArray, '', endLabel, false, true);
  activityArray = obj[0];
  const activityIDEnd = obj[1];
  activityIndexArray = obj[2];

  let previousTask = '';
  let previousActivityID = '';
  let currentActivityID = '';
  let variant = '';
  let currentTimestamp = '';
  let previousTimestamp = '';
  let variantID = '';
  let variantIndex = 0;
  let eventVariantArray = [];
  let min_time = '3000'; // year :-)
  let max_time = '1000';
  let sequence = 0;
  let duration_seconds = 0;
  let startEventIndex;
  let finishEventIndex;
  let vtp = {};
  let steps = 0;
  let taskID = 0;

  let year;
  let month;
  let day;

  for (let i = 0; i < sorted.length; i++) {
    if (variantArray.length >= 5000) {
      // UI limit is enforced in the caller as well; keep guard here.
      break;
    }
    if (planTier !== 'free' && sample == null && i >= 500000) {
      break;
    }

    if (sorted[i].task !== previousTask) {
      if (i > 1) {
        let loops = 0;
        for (let x = 0; x < taskEventsArray.length; x++) {
          for (let y = 0; y < x; y++) {
            if (taskEventsArray[x].t === taskEventsArray[y].t) {
              loops++;
            }
          }
        }

        obj = arrayVariantID(variantArray, variantArrayIndex, variant, loops, taskEventsArray.length);
        variantArray = obj[0];
        variantID = obj[1];
        variantIndex = obj[2];
        variantArrayIndex = obj[3];

        for (let x = 0; x < taskEventsArray.length; x++) {
          if (x === 0) startEventIndex = taskEventsArray[x].i;
          if (x === taskEventsArray.length - 1) finishEventIndex = taskEventsArray[x].i;
          vtp = { i: taskEventsArray[x].i, v: variantID, vid: variantIndex };
          eventVariantArray.push(vtp);
        }
        eventVariantArray.push({ i: finishEventIndex + 1, v: variantID, vid: variantIndex });

        sequence++;
        obj = importArrayStepUpdate(stepArray, stepArrayIndex, previousActivityID, activityIDEnd);
        stepArray = obj[0];
        stepArrayIndex = obj[2];

        vtp = {
          f: previousActivityID,
          t: activityIDEnd,
          v: variantID,
          s: sequence,
          u: sorted[i - 1].resource != null ? sorted[i - 1].resource : 'n/a',
          d: 0,
          ac: 0,
          cc: 0,
          pc: 0,
          at: sorted[i - 1].activity_type,
          pd: 0,
          discovery_rules: [],
          costs: [],
          actual_cost: 0,
          forecast_cost: 0,
          forecast_cost_exists: false,
          k: previousTask,
          ts: previousTimestamp,
          rd: previousTimestamp.substring(0, 10),
          tid: taskID,
          sliced: true,
          vid: -1,
          elid: eventLogArray.length,
        };

        for (let j = 0; j < taskAttributes.length; j++) {
          const key = 'ta' + j;
          vtp['ta' + j] = sorted[i - 1][key];
        }
        for (let j = 0; j < eventAttributes.length; j++) {
          const key = eventAttributes[j].field === 'Resource' ? 'resource' : 'ea' + j;
          vtp['ea' + j] = sorted[i - 1][key];
        }
        vtp.comments_si = sorted[i].comments_si;
        vtp.comments_fi = sorted[i].comments_fi;
        vtp.worklog_si = sorted[i].worklog_si;
        vtp.worklog_fi = sorted[i].worklog_fi;

        eventLogArray.push(vtp);

        if (sorted[i - 1].create_time !== '' && sorted[i - 1].create_time != null) {
          min_time = sorted[i - 1].create_time;
        }
        if (sorted[i - 1].close_time !== '' && sorted[i - 1].close_time != null) {
          max_time = sorted[i - 1].close_time;
        }

        vtp = {
          k: sorted[i - 1].task,
          t1: min_time,
          rd: min_time.substring(0, 10),
          t2: max_time,
          d: 0,
          a: sequence - 1,
          v: variantID,
          summary: sorted[i - 1].summary,
          si: startEventIndex,
          fi: finishEventIndex + 1,
          vid: variantIndex,
          id: taskID,
          comments_si: sorted[i - 1].comments_si,
          comments_fi: sorted[i - 1].comments_fi,
          worklog_si: sorted[i - 1].worklog_si,
          worklog_fi: sorted[i - 1].worklog_fi,
        };

        year = parseInt(min_time.substring(0, 4));
        month = parseInt(min_time.substring(5, 7));
        day = parseInt(min_time.substring(8, 10));
        vtp.utc = Date.UTC(year, month - 1, day);

        if (calendarExists === true) {
          duration_seconds = calculateDuration(min_time, max_time, sorted[i - 1].task);
        } else {
          const date1 = new Date(min_time);
          const date2 = new Date(max_time);
          duration_seconds = (date2 - date1) / 1000;
        }
        vtp.d = duration_seconds;

        for (let j = 0; j < taskAttributes.length; j++) {
          const key = 'ta' + j;
          vtp['ta' + j] = sorted[i - 1][key];
        }
        for (let j = 0; j < metricArray.length; j++) {
          const key = 'm' + j;
          vtp['m' + j] = sorted[i - 1][key];
        }

        taskArray.push(vtp);
        taskID++;
        taskEventsArray = [];
      }

      previousActivityID = activityIDStart;
      previousTimestamp = '';
      sequence = 0;
      variant = '';
      min_time = '3000';
      max_time = '1000';
    }

    sequence++;
    duration_seconds = 0;

    currentTimestamp = sorted[i].timestamp.replace('T', ' ');

    if (previousTimestamp !== '') {
      if (calendarExists === true) {
        duration_seconds = calculateDuration(previousTimestamp, currentTimestamp);
      } else {
        const date1 = new Date(previousTimestamp);
        const date2 = new Date(currentTimestamp);
        duration_seconds = (date2 - date1) / 1000;
      }
    }

    if (sorted[i].create_time === '' || sorted[i].create_time == null) {
      if (currentTimestamp < min_time) min_time = currentTimestamp;
    }
    if (sorted[i].close_time === '' || sorted[i].close_time == null) {
      if (currentTimestamp > max_time) max_time = currentTimestamp;
    }

    obj = importArrayActivityID(
      activityArray,
      activityIndexArray,
      sorted[i].activity_type,
      sorted[i].activity,
      false,
      false
    );
    activityArray = obj[0];
    currentActivityID = obj[1];
    activityIndexArray = obj[2];

    obj = importArrayStepUpdate(stepArray, stepArrayIndex, previousActivityID, currentActivityID);
    stepArray = obj[0];
    stepArrayIndex = obj[2];

    vtp = {
      f: previousActivityID,
      t: currentActivityID,
      s: sequence,
      u: sorted[i].resource != null ? sorted[i].resource : 'n/a',
      d: duration_seconds,
      ac: 0,
      cc: 0,
      pc: 0,
      pd: 0,
      discovery_rules: [],
      costs: [],
      actual_cost: 0,
      forecast_cost: 0,
      forecast_cost_exists: false,
      k: sorted[i].task,
      at: sorted[i].activity_type,
      ts: currentTimestamp,
      rd: currentTimestamp.substring(0, 10),
      v: -1,
      tid: taskID,
      sliced: true,
      vid: -1,
      elid: eventLogArray.length,
      comments_si: sorted[i].comments_si,
      comments_fi: sorted[i].comments_fi,
      worklog_si: sorted[i].worklog_si,
      worklog_fi: sorted[i].worklog_fi,
    };

    for (let j = 0; j < taskAttributes.length; j++) {
      const key = 'ta' + j;
      vtp['ta' + j] = sorted[i][key];
    }
    for (let j = 0; j < eventAttributes.length; j++) {
      const key = eventAttributes[j].field === 'Resource' ? 'resource' : 'ea' + j;
      vtp['ea' + j] = sorted[i][key];
    }

    eventLogArray.push(vtp);

    taskEventsArray.push({ i: eventLogArray.length - 1, f: previousActivityID, t: currentActivityID });

    if (variant !== '') variant = variant + activity_separator;
    variant = variant + sorted[i].activity;

    previousTask = sorted[i].task;
    previousTimestamp = currentTimestamp;
    previousActivityID = currentActivityID;
  }

  // Insert last record
  if (sorted.length > 0) {
    obj = arrayVariantID(variantArray, variantArrayIndex, variant, steps, taskEventsArray.length);
    variantArray = obj[0];
    variantID = obj[1];
    variantIndex = obj[2];
    variantArrayIndex = obj[3];

    for (let x = 0; x < taskEventsArray.length; x++) {
      if (x === 0) startEventIndex = taskEventsArray[x].i;
      if (x === taskEventsArray.length - 1) finishEventIndex = taskEventsArray[x].i;
      eventVariantArray.push({ i: taskEventsArray[x].i, v: variantID, vid: variantIndex });
    }
    eventVariantArray.push({ i: finishEventIndex + 1, v: variantID, vid: variantIndex });

    sequence++;
    obj = importArrayStepUpdate(stepArray, stepArrayIndex, previousActivityID, currentActivityID);
    stepArray = obj[0];
    stepArrayIndex = obj[2];

    // End event
    const last = sorted[sorted.length - 1];
    vtp = {
      f: previousActivityID,
      t: activityIDEnd,
      v: -1,
      s: sequence,
      u: last.resource != null ? last.resource : 'n/a',
      d: 0,
      ac: 0,
      cc: 0,
      pc: 0,
      pd: 0,
      discovery_rules: [],
      costs: [],
      actual_cost: 0,
      forecast_cost_exists: false,
      forecast_cost: 0,
      k: previousTask,
      at: last.activity_type,
      ts: previousTimestamp,
      rd: previousTimestamp.substring(0, 10),
      tid: taskID,
      vid: -1,
      elid: eventLogArray.length,
      comments_si: last.comments_si,
      comments_fi: last.comments_fi,
      worklog_si: last.worklog_si,
      worklog_fi: last.worklog_fi,
    };

    year = parseInt(previousTimestamp.substring(0, 4));
    month = parseInt(previousTimestamp.substring(5, 7));
    day = parseInt(previousTimestamp.substring(8, 10));
    vtp.ts_utc = Date.UTC(year, month - 1, day);

    for (let j = 0; j < taskAttributes.length; j++) {
      const key = 'ta' + j;
      vtp['ta' + j] = last[key];
    }
    for (let j = 0; j < eventAttributes.length; j++) {
      const key = eventAttributes[j].field === 'Resource' ? 'resource' : 'ea' + j;
      vtp['ea' + j] = last[key];
    }

    eventLogArray.push(vtp);

    if (last.create_time !== '' && last.create_time != null) min_time = last.create_time;
    if (last.close_time !== '' && last.close_time != null) max_time = last.close_time;

    const taskObj = {
      k: last.task,
      t1: min_time,
      rd: min_time.substring(0, 10),
      t2: max_time,
      d: 0,
      a: sequence - 1,
      v: variantID,
      si: startEventIndex,
      fi: finishEventIndex + 1,
      vid: variantIndex,
      id: taskID,
      summary: last.summary,
      comments_si: last.comments_si,
      comments_fi: last.comments_fi,
      worklog_si: last.worklog_si,
      worklog_fi: last.worklog_fi,
    };

    year = parseInt(min_time.substring(0, 4));
    month = parseInt(min_time.substring(5, 7));
    day = parseInt(min_time.substring(8, 10));
    taskObj.utc = Date.UTC(year, month - 1, day);

    if (calendarExists === true) {
      duration_seconds = calculateDuration(min_time, max_time, last.task);
    } else {
      const date1 = new Date(min_time);
      const date2 = new Date(max_time);
      duration_seconds = (date2 - date1) / 1000;
    }
    taskObj.d = duration_seconds;

    for (let j = 0; j < taskAttributes.length; j++) {
      const key = 'ta' + j;
      taskObj['ta' + j] = last[key];
    }
    for (let j = 0; j < metricArray.length; j++) {
      const key = 'm' + j;
      taskObj['m' + j] = last[key];
    }

    taskArray.push(taskObj);
  }

  // Apply variant ids back onto eventLogArray
  for (let x = 0; x < eventVariantArray.length; x++) {
    const idx = eventVariantArray[x].i;
    if (eventLogArray[idx]) {
      eventLogArray[idx].v = eventVariantArray[x].v;
      eventLogArray[idx].vid = eventVariantArray[x].vid;
    }
  }

  // Assign deterministic step ids
  for (let j = 0; j < stepArray.length; j++) {
    stepArray[j].id = j;
  }

  // Comments/worklogs association (kept here because it depends on event boundaries)
  let startNode = 0;
  let endNode = 0;
  for (let j = 0; j < activityArray.length; j++) {
    if (activityArray[j].start_node === true) startNode = activityArray[j].id;
    if (activityArray[j].end_node === true) endNode = activityArray[j].id;
  }

  taskArray.sort((a, b) => a.id - b.id);
  eventLogArray.sort((a, b) => a.k.localeCompare(b.k) || a.ts.localeCompare(b.ts));

  for (let i = 0; i < eventLogArray.length; i++) {
    let startTimeStamp = '';
    if (eventLogArray[i].f === startNode) {
      startTimeStamp = taskArray[eventLogArray[i].tid]?.t1 || '';
    } else {
      startTimeStamp = eventLogArray[i - 1]?.ts || '';
    }

    if (eventLogArray[i].t !== endNode && eventLogArray[i].f !== startNode) {
      const endTimeStamp = eventLogArray[i].ts;

      const eventComments = [];
      const eventWorklog = [];

      if (eventLogArray[i].comments_si !== 'null' && eventLogArray[i].comments_si != null) {
        for (let c = eventLogArray[i].comments_si; c <= eventLogArray[i].comments_fi; c++) {
          if (issueComments[c]?.timestamp >= startTimeStamp && issueComments[c]?.timestamp < endTimeStamp) {
            eventComments.push(issueComments[c]);
            issueComments[c].f = eventLogArray[i].f;
            issueComments[c].t = eventLogArray[i].t;
          }
        }
      }

      if (eventLogArray[i].worklog_si !== 'null' && eventLogArray[i].worklog_si != null) {
        for (let w = eventLogArray[i].worklog_si; w <= eventLogArray[i].worklog_fi; w++) {
          if (issueWorklogs[w]?.timestamp >= startTimeStamp && issueWorklogs[w]?.timestamp < endTimeStamp) {
            eventWorklog.push(issueWorklogs[w]);
            issueWorklogs[w].f = eventLogArray[i].f;
            issueWorklogs[w].t = eventLogArray[i].t;
          }
        }
      }

      eventLogArray[i].comments = eventComments;
      eventLogArray[i].worklog = eventWorklog;
    } else {
      eventLogArray[i].comments = [];
      eventLogArray[i].worklog = [];
    }
  }

  for (let i = 0; i < taskArray.length; i++) {
    const eventComments = [];
    const eventWorklog = [];

    for (let c = taskArray[i].comments_si; c <= taskArray[i].comments_fi; c++) {
      if (issueComments[c] != null) eventComments.push(issueComments[c]);
    }
    for (let w = taskArray[i].worklog_si; w <= taskArray[i].worklog_fi; w++) {
      if (issueWorklogs[w] != null) eventWorklog.push(issueWorklogs[w]);
    }

    taskArray[i].comments = eventComments;
    taskArray[i].worklog = eventWorklog;
  }

  // Build per-variant event list
  const variantEventsArray = [];
  eventLogArray.sort((a, b) => a.vid - b.vid);

  let vid = -1;
  let eventStr = '';
  for (let i = 0; i < eventLogArray.length; i++) {
    if (i > 0 && eventLogArray[i].vid !== vid) {
      variantEventsArray.push({ vid, events: eventStr });
      eventStr = '';
    }
    if (eventStr !== '') eventStr = eventStr + '^';
    eventStr = eventStr + eventLogArray[i].elid;
    vid = eventLogArray[i].vid;
  }
  variantEventsArray.push({ vid, events: eventStr });

  return {
    activityArray,
    activityIndexArray,
    stepArray,
    variantArray,
    taskArray,
    eventLogArray,
    variantEventsArray,
    issueComments,
    issueWorklogs,
  };
}
